import { QueryClient, queryOptions, skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MeResponse } from '@pathonline/shared';
import { useState } from 'react';

export class HttpError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** A refused request (4xx) gives the same answer again: no retry. A 5xx or no answer: at most 2 retries. */
export function retryRule(failureCount: number, error: unknown): boolean {
  if (error instanceof HttpError && error.status < 500) return false;
  return failureCount < 2;
}

export const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: retryRule } },
});

const ME_URL = '/api/me';

/**
 * The message for an error answer that is not the API's own { error } (the sign-in layer, a gateway).
 * `visitor`: nobody was signed in (me is null), so a 401 asks to sign in, not to sign in again.
 */
export function statusMessage(status: number, visitor = false): string {
  if (status === 401) return visitor ? 'Sign in to see this.' : 'Your session has ended — sign in again with the link at the top, then try once more.';
  if (status === 403) return 'Your role cannot do this.';
  if (status === 404) return 'Not found.';
  if (status >= 500) return `The server is not answering (HTTP ${status}). Try again in a minute.`;
  return `Request failed (HTTP ${status}).`;
}

/** true: a 401 came while someone was signed in (see failure); recheckSession sets it back. Exported for the tests. */
export const sessionEndedQuery = queryOptions<boolean>({ queryKey: ['session-ended'], queryFn: skipToken });
const SESSION_ENDED = sessionEndedQuery.queryKey;

/**
 * The error for a failed answer. A 401 means the session ended:
 *   someone was signed in    the page stays as it is (typed text too) and Layout shows the "sign in again" bar;
 *                            /api/me's own 401 too (it may come first: a page mounting, the network back)
 *   nobody, or not known yet /api/me is asked again (not for its own 401: no loop), so the app shows sign-in
 */
async function failure(url: string, res: Response): Promise<HttpError> {
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  const me = queryClient.getQueryData<MeResponse | null>(['me']);
  if (res.status === 401) {
    if (me) queryClient.setQueryData(SESSION_ENDED, true);
    else if (url !== ME_URL) void queryClient.invalidateQueries({ queryKey: ['me'] });
  }
  return new HttpError(res.status, body?.error || statusMessage(res.status, me === null));
}

export async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw await failure(url, res);
  return (await res.json()) as T;
}

/** The file name in a Content-Disposition header; `fallback` when there is none. */
export function fileNameOf(disposition: string | null, fallback: string): string {
  return /filename="([^"]+)"/.exec(disposition ?? '')?.[1] ?? fallback;
}

/** GET a file and save it: the browser's download, named as the server says. Errors as getJson. The file name. */
export async function downloadFile(url: string, fallback: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw await failure(url, res);
  const name = fileNameOf(res.headers.get('content-disposition'), fallback);
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  // Revoked later: the browser may still be reading it.
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
  return name;
}

/** What useMe asks; exported for the tests. */
export const meQuery = {
  queryKey: ['me'],
  queryFn: async (): Promise<MeResponse | null> => {
    try {
      return await getJson<MeResponse>(ME_URL);
    } catch (e) {
      if (!(e instanceof HttpError && e.status === 401)) throw e;
      // The "sign in again" bar is up: keep the person the page was opened as, so the page is not replaced.
      return queryClient.getQueryData(SESSION_ENDED) === true ? (queryClient.getQueryData<MeResponse | null>(['me']) ?? null) : null;
    }
  },
  staleTime: 5 * 60_000,
};

/**
 * The signed-in person, or null when nobody is signed in (the site answers 401).
 * A 5xx or no answer is retried by the default rule (retryRule), a 403 is not.
 */
export function useMe() {
  return useQuery(meQuery);
}

/**
 * The site is read-only (READ_ONLY: /api/me says so). From the /api/me already loaded, never asked for
 * here; it works outside a QueryClientProvider too (the chart's tests draw it without one).
 */
export function useReadOnly(): boolean {
  return useQuery({ ...meQuery, enabled: false }, queryClient).data?.readOnly === true;
}

/** The session ended while a page was open (a 401 after sign-in): Layout shows the "sign in again" bar. */
export function useSessionEnded(): boolean {
  return useQuery(sessionEndedQuery).data === true;
}

let rechecking: Promise<void> | null = null;

/**
 * The bar is up and the person is back on this tab (after signing in again in the other one): ask /api/me.
 *   still 401, or no answer  the bar stays
 *   the same person          the bar goes; the page and its typed text stay; what failed with the 401 is
 *                            asked again (a page on screen now, one not shown on its next visit)
 *   someone else             reload: the page holds the first person's data
 */
export function recheckSession(): Promise<void> {
  rechecking ??= (async () => {
    const before = queryClient.getQueryData<MeResponse | null>(['me']);
    const now = await getJson<MeResponse>(ME_URL).catch(() => null);
    if (!now) return;
    if (before && now.email.toLowerCase() === before.email.toLowerCase()) {
      queryClient.setQueryData(['me'], now);
      queryClient.setQueryData(SESSION_ENDED, false);
      void queryClient.invalidateQueries({ predicate: (q) => q.state.error instanceof HttpError && q.state.error.status === 401 });
    } else location.reload();
  })().finally(() => {
    rechecking = null;
  });
  return rechecking;
}

/** A page's bundle: GET /api/views/{name}, under the query key ['views', name]. */
export function useView<T>(name: string, staleTime = 30_000) {
  return useQuery({ queryKey: ['views', name], queryFn: () => getJson<T>(`/api/views/${name}`), staleTime });
}

/** State kept in sessionStorage under `key` for this tab: stored fields over `defaults`. */
export function useStoredState<T extends object>(key: string, defaults: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? { ...defaults, ...(JSON.parse(raw) as Partial<T>) } : defaults;
    } catch {
      return defaults;
    }
  });
  const store = (v: T) => {
    setValue(v);
    try {
      sessionStorage.setItem(key, JSON.stringify(v));
    } catch {
      /* private window: just not remembered */
    }
  };
  return [value, store];
}

/**
 * What useUnread asks; exported for the tests. The nav badge stays mounted for the whole visit, so it
 * asks again every 2 minutes while the tab is shown, and on coming back to the tab if the count is
 * 2 minutes old or more.
 */
export const unreadQuery = {
  queryKey: ['unread'],
  queryFn: () => getJson<{ count: number }>('/api/comments/unread'),
  staleTime: 2 * 60_000,
  refetchInterval: 2 * 60_000,
  refetchOnWindowFocus: true,
};

/** New comments for the nav badge; 0 when the person may not read comments. */
export function useUnread(enabled: boolean) {
  return useQuery({ ...unreadQuery, enabled });
}

/**
 * POST / PATCH / PUT / DELETE with a JSON body; the server's error message (else statusMessage) becomes
 * the Error's message. No body given = {}: a proxy may drop the Content-Type of an empty request, and the
 * API answers a write without JSON with 415.
 */
export async function sendJson<T = unknown>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) throw await failure(url, res);
  return (res.status === 204 ? undefined : await res.json()) as T;
}

export type Note = { bad: boolean; text: string };

/**
 * Saves with one status line. `refresh`: query keys refetched after a save. `defaults.saving`: shown while
 * a save is on its way. `defaults.ok`: the success text when the call gives none (none at all = the line is cleared).
 */
export function useRun(refresh: string[][] = [['views']], defaults: { saving?: string; ok?: string } = {}) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (f: () => Promise<unknown>) => f(),
    onSuccess: () => Promise.all(refresh.map((key) => qc.invalidateQueries({ queryKey: key }))),
  });
  const [msg, setMsg] = useState<Note | null>(null);
  const [pending, setPending] = useState(0);
  /**
   * `ok` may read the answer. `after` runs on success only, once the views are refetched, with what `f`
   * answered (e.g. to clear an add form: on an error the typed text stays); `failed` on an error.
   * mutateAsync, not mutate(f, { onSuccess }): a second save started before the first answers would drop
   * the first one's callbacks, and its `after` would never run. `pending` counts every save on its way.
   */
  const run = (f: () => Promise<unknown>, ok?: string | ((answer: unknown) => string), after?: (answer: unknown) => void, failed?: () => void) => {
    if (defaults.saving) setMsg({ bad: false, text: defaults.saving });
    setPending((n) => n + 1);
    void save
      .mutateAsync(f)
      .then(
        (answer) => {
          const text = typeof ok === 'function' ? ok(answer) : (ok ?? defaults.ok);
          setMsg(text ? { bad: false, text } : null);
          after?.(answer);
        },
        (e: Error) => {
          setMsg({ bad: true, text: e.message });
          failed?.();
        },
      )
      .finally(() => setPending((n) => n - 1));
  };
  return { run, busy: pending > 0, msg };
}

/** useRun's save, as a prop. */
export type Run = ReturnType<typeof useRun>['run'];

export function signInUrl(returnTo: string = location.pathname + location.search): string {
  return `/.auth/login/externalid?post_login_redirect_uri=${encodeURIComponent(returnTo)}`;
}
export const SIGN_OUT_URL = '/.auth/logout?post_logout_redirect_uri=/';
